import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AcceptingStatus, Availability, WaitBucket } from "@/lib/availability";
import type { TeachingTile } from "@/lib/learn";
import type { Facility } from "@/lib/public-directory";

export type MemberRole = "gp" | "specialist";
export type CpdActivityType =
  | "educational"
  | "reviewing_performance"
  | "measuring_outcomes";

export interface BoardMemberSummary {
  displayName: string;
  role: MemberRole;
  onboarded: boolean;
}

export interface BoardAlert {
  id: string;
  title: string;
  level: number;
  publishedAt: string;
  sourceUrl: string;
}

export interface BoardAvailabilityChange {
  facilityId: string;
  facilitySlug: string;
  name: string;
  specialty: string;
  practice: string;
  accepting: AcceptingStatus;
  waitBucket: WaitBucket | null;
  changedAt: string;
}

export interface BoardRecentFacility {
  facilitySlug: string;
  name: string;
  specialty: string;
  accepting: AcceptingStatus;
  waitBucket: WaitBucket | null;
  confirmedAt: string;
}

export interface BoardSavedSummary {
  facilitySlug: string;
  name: string;
  specialty: string;
  availability: Availability | null;
  memberUserId?: string | null;
}

export interface BoardMessageLatest {
  conversationId: string;
  from: {
    display: string;
    handle: string;
    role: string;
  };
  excerpt: string;
  at: string;
}

export interface BoardHomeResponse {
  member: BoardMemberSummary;
  goodMorning: {
    alerts: BoardAlert[];
    changes: BoardAvailabilityChange[];
    joinedThisWeek: number;
  };
  nextPatient: {
    acceptingCount: number;
    recentlyConfirmed: BoardRecentFacility[];
    saved: BoardSavedSummary[];
  };
  beforeYouLeave: {
    tile: {
      id: string;
      title: string;
      specialistName: string;
      specialtyName: string;
      minutes: number;
    } | null;
    cpdMinutesThisMonth: number;
    cpdMinutesThisYear: number;
    roundsUnread: number;
  };
  messages?: {
    unread: number;
    latest: BoardMessageLatest[];
  };
  weekInRounds?: {
    newThreads: number;
    repliesToMine: number;
    questionsWaiting: number;
  };
}

export interface BoardMe {
  id: string;
  displayName: string;
  handle: string;
  role: MemberRole;
  email: string;
  specialtyInterests: string[];
  onboardedAt: string | null;
  boardPrefs: Record<string, unknown>;
}

export interface BoardMePatch {
  displayName?: string;
  handle?: string;
  specialtyInterests?: string[];
  boardPrefs?: Record<string, unknown>;
}

export type BoardTeachingTile = TeachingTile & {
  viewed: boolean;
  completed: boolean;
  cpdLogged: boolean;
};

export interface BoardLearnResponse {
  tiles: BoardTeachingTile[];
}

export interface CpdEntry {
  id: string;
  tileId: string | null;
  activityType: CpdActivityType;
  minutes: number;
  title: string;
  note: string;
  loggedOn: string;
}

export interface CpdTotals {
  educational: number;
  reviewing_performance: number;
  measuring_outcomes: number;
  minutes: number;
  hours: number;
}

export interface CpdResponse {
  entries: CpdEntry[];
  totals: CpdTotals;
}

export interface CreateCpdEntry {
  tileId: string | null;
  activityType: CpdActivityType;
  minutes: number;
  title: string;
  note: string;
  loggedOn: string;
}

export interface SavedFacilitiesResponse {
  items: Facility[];
}

export function useBoardHome(citySlug: string) {
  return useQuery({
    queryKey: ["board-home", citySlug],
    queryFn: () => api.get<BoardHomeResponse>(`/${citySlug}/v1/board/home`),
    enabled: Boolean(citySlug),
  });
}

export function useBoardMe(citySlug: string) {
  return useQuery({
    queryKey: ["board-me", citySlug],
    queryFn: () => api.get<BoardMe>(`/${citySlug}/v1/board/me`),
    enabled: Boolean(citySlug),
  });
}

export function useUpdateBoardMe(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BoardMePatch) =>
      api.patch<BoardMe>(`/${citySlug}/v1/board/me`, body),
    onSuccess: (member) => {
      queryClient.setQueryData(["board-me", citySlug], member);
      void queryClient.invalidateQueries({ queryKey: ["board-home", citySlug] });
    },
  });
}

export function useSavedFacilities(citySlug: string, enabled = true) {
  return useQuery({
    queryKey: ["board-saved", citySlug],
    queryFn: () =>
      api.get<SavedFacilitiesResponse>(`/${citySlug}/v1/board/saved`),
    enabled: enabled && Boolean(citySlug),
  });
}

export function useBoardLearn(citySlug: string) {
  return useQuery({
    queryKey: ["board-learn", citySlug],
    queryFn: () => api.get<BoardLearnResponse>(`/${citySlug}/v1/board/learn`),
    enabled: Boolean(citySlug),
  });
}

export function useMarkTileViewed(citySlug: string, tileId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: true }>(
        `/${citySlug}/v1/board/learn/${encodeURIComponent(tileId)}/view`,
        { completed: true },
      ),
    onSuccess: () => {
      queryClient.setQueryData<BoardLearnResponse>(
        ["board-learn", citySlug],
        (current) =>
          current
            ? {
                tiles: current.tiles.map((tile) =>
                  tile.id === tileId
                    ? { ...tile, viewed: true, completed: true }
                    : tile,
                ),
              }
            : current,
      );
    },
  });
}

export function useBoardCpd(citySlug: string, year: number) {
  return useQuery({
    queryKey: ["board-cpd", citySlug, year],
    queryFn: () =>
      api.get<CpdResponse>(`/${citySlug}/v1/board/cpd`, { params: { year } }),
    enabled: Boolean(citySlug && year),
  });
}

export function useCreateCpdEntry(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCpdEntry) =>
      api.post<CpdEntry>(`/${citySlug}/v1/board/cpd`, body),
    onSuccess: (entry) => {
      queryClient.setQueryData<BoardLearnResponse>(
        ["board-learn", citySlug],
        (current) =>
          current && entry.tileId
            ? {
                tiles: current.tiles.map((tile) =>
                  tile.id === entry.tileId ? { ...tile, cpdLogged: true } : tile,
                ),
              }
            : current,
      );
      void queryClient.invalidateQueries({ queryKey: ["board-cpd", citySlug] });
      void queryClient.invalidateQueries({ queryKey: ["board-home", citySlug] });
    },
  });
}

export function useDeleteCpdEntry(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) =>
      api.delete<{ ok: true }>(
        `/${citySlug}/v1/board/cpd/${encodeURIComponent(entryId)}`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["board-cpd", citySlug] });
      void queryClient.invalidateQueries({ queryKey: ["board-home", citySlug] });
      void queryClient.invalidateQueries({ queryKey: ["board-learn", citySlug] });
    },
  });
}

export function calculateCpdTotals(
  entries: ReadonlyArray<Pick<CpdEntry, "activityType" | "minutes">>,
): CpdTotals {
  const totals: CpdTotals = {
    educational: 0,
    reviewing_performance: 0,
    measuring_outcomes: 0,
    minutes: 0,
    hours: 0,
  };

  for (const entry of entries) {
    totals[entry.activityType] += entry.minutes;
    totals.minutes += entry.minutes;
  }
  totals.hours = totals.minutes / 60;
  return totals;
}

const MEDICARE_NUMBER = /\b\d{4}\s?\d{5}\s?\d{1}\b/;
const DOB_LIKE_WITH_CONTEXT =
  /(?:\b(?:patient|pt)\b.{0,24}\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b|\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b.{0,24}\b(?:patient|pt)\b)/i;

export function containsPatientIdentifier(value: string): boolean {
  return MEDICARE_NUMBER.test(value) || DOB_LIKE_WITH_CONTEXT.test(value);
}

export function todayIsoDate(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function suggestHandle(value: string): string {
  const handle = value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24);
  return handle.length >= 3 ? handle : "clinician";
}

export function formatWeekInRoundsLine(
  counts: NonNullable<BoardHomeResponse["weekInRounds"]>,
): string {
  const parts: string[] = [];

  if (counts.newThreads > 0) {
    parts.push(
      `${counts.newThreads} new ${counts.newThreads === 1 ? "thread" : "threads"}`,
    );
  }
  if (counts.repliesToMine > 0) {
    parts.push(
      `${counts.repliesToMine} ${counts.repliesToMine === 1 ? "reply" : "replies"} to yours`,
    );
  }
  if (counts.questionsWaiting > 0) {
    parts.push(`${counts.questionsWaiting} waiting for a specialist`);
  }

  return parts.join(" · ");
}
